import { useState, useCallback, useEffect } from 'react';
import { IconPlus, IconCheck, IconLoader2, IconAlertCircle } from '@tabler/icons-react';
import { v4 as uuidv4 } from 'uuid';
import { useFamilyHistory } from '../hooks/useFamilyHistory';
import { useSectionCompletion } from '../hooks/useSectionCompletion';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import { FamilyMemberForm } from '../components/familyHistory/FamilyMemberForm';
import type { FamilyMemberLocal, FamilyHistoryData, FamilyRelationship } from '../types/familyHistory';

// Extended type to track new (unsaved) items
interface FamilyMemberWithNew extends FamilyMemberLocal {
  isNewItem?: boolean;
}

// Factory function for new items
function createEmptyFamilyMember(): FamilyMemberWithNew {
  return {
    localId: uuidv4(),
    relationship: '' as FamilyRelationship, // Will be set by user
    isDeceased: false,
    conditions: [],
    isNewItem: true,
  };
}

export function FamilyHistory() {
  const { returnToDashboard } = useSectionCompletion('matrix');
  const {
    familyHistoryData,
    isLoading,
    isSaving,
    error,
    hasFamilyMembers,
    reviewedFlags,
    setReviewedFlag,
    saveAll,
  } = useFamilyHistory();

  // Local state for editing
  const [familyMembers, setFamilyMembers] = useState<FamilyMemberWithNew[]>([]);

  // Sync local state when data loads
  useEffect(() => {
    if (!isLoading) {
      setFamilyMembers(
        familyHistoryData.familyMembers.map((m) => ({ ...m, isNewItem: false }))
      );
    }
  }, [isLoading, familyHistoryData]);

  // === FAMILY MEMBER HANDLERS ===
  const handleAddFamilyMember = useCallback(() => {
    setFamilyMembers((prev) => [...prev, createEmptyFamilyMember()]);
  }, []);

  const handleUpdateFamilyMember = useCallback(
    (localId: string, updated: FamilyMemberWithNew) => {
      setFamilyMembers((prev) =>
        prev.map((m) => (m.localId === localId ? updated : m))
      );
    },
    []
  );

  const handleRemoveFamilyMember = useCallback((localId: string) => {
    setFamilyMembers((prev) => {
      const item = prev.find((m) => m.localId === localId);
      if (item?.isNewItem) {
        return prev.filter((m) => m.localId !== localId);
      }
      return prev.map((m) =>
        m.localId === localId ? { ...m, isDeleted: true } : m
      );
    });
  }, []);

  const handleSubmitFamilyMember = useCallback((localId: string) => {
    setFamilyMembers((prev) =>
      prev.map((m) =>
        m.localId === localId ? { ...m, isNewItem: false } : m
      )
    );
  }, []);

  // === TOGGLE "NO FAMILY HISTORY" ===
  const handleToggleNoFamilyHistory = useCallback(() => {
    setReviewedFlag('noFamilyHistory', !reviewedFlags.noFamilyHistory);
  }, [reviewedFlags.noFamilyHistory, setReviewedFlag]);

  // === SAVE ===
  const handleSave = async () => {
    const data: FamilyHistoryData = {
      familyMembers: familyMembers.filter((m) => !m.isNewItem),
    };

    const success = await saveAll(data);
    if (success) {
      showSuccessToast('Family history saved');
      returnToDashboard();
    } else {
      showErrorToast('Unable to save. Please try again.');
    }
  };

  // Check for unsaved items (still in edit mode)
  const hasUnsavedItems = familyMembers.some((m) => m.isNewItem && !m.isDeleted);

  // Navigation guard for unsaved items
  useUnsavedChangesGuard(hasUnsavedItems);

  // Visible items (filter out deleted)
  const visibleFamilyMembers = familyMembers.filter((m) => !m.isDeleted);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <IconLoader2 className="animate-spin text-cyan-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Family History
        </h2>
        <p className="text-gray-400">
          Add family members and their health conditions to help us understand hereditary health patterns.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3 text-red-400">
          <IconAlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Family Members Section */}
      <section>
        <div className="space-y-4">
          {/* Empty state */}
          {visibleFamilyMembers.length === 0 && !hasFamilyMembers && (
            <div className="p-6 border border-dashed border-gray-700 rounded-lg text-center text-gray-500">
              <p className="mb-2">No family members added yet.</p>
              <p className="text-sm">
                Add information about parents, siblings, grandparents, and other relatives
                to help us understand your family health history.
              </p>
            </div>
          )}

          {/* Family member items */}
          {visibleFamilyMembers.map((member) => (
            <FamilyMemberForm
              key={member.localId}
              member={member}
              onChange={(updated) => handleUpdateFamilyMember(member.localId, updated)}
              onRemove={() => handleRemoveFamilyMember(member.localId)}
              onSubmit={() => handleSubmitFamilyMember(member.localId)}
              isNew={member.isNewItem}
              disabled={isSaving}
            />
          ))}

          {/* Add button + No family history */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAddFamilyMember}
              disabled={isSaving || reviewedFlags.noFamilyHistory}
              className="flex-1 p-3 border border-dashed border-gray-700 rounded-lg text-gray-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <IconPlus size={16} />
              Add Family Member
            </button>

            {!hasFamilyMembers && (
              <button
                type="button"
                onClick={handleToggleNoFamilyHistory}
                disabled={isSaving}
                className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 ${
                  reviewedFlags.noFamilyHistory
                    ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                    : 'border border-dashed border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                <IconCheck size={16} />
                No family history to report
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-800">
        <div>
          {hasUnsavedItems && (
            <p className="text-sm text-yellow-500">
              Please save or cancel all items before proceeding.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || hasUnsavedItems}
          className="btn disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSaving && <IconLoader2 className="animate-spin" size={16} />}
          Save
        </button>
      </div>
    </div>
  );
}
